import React, {useMemo} from 'react'
import PropTypes from 'prop-types'

// Components
import {StarIcon, StarFilledIcon} from '../../leap/components/icons/index'
import {Flex, VisuallyHidden, Text, useSlotRecipe, Box, Button} from '@chakra-ui/react3'

//Constants
import {RATING_MAX_STARS} from '../../constants'
import useOcapiSite from '../../hooks/useOcapiSite'
import RatingSkeleton from './skeleton'
import {noop} from '../../utils/utils'
import {useIntl} from 'react-intl'
import {ratingsTextRecipe} from './index.recipe'

export const Ratings = (props) => {
    const {formatMessage} = useIntl()
    const {count, rating, onRatingsClick = noop, variant, ...ratingsProps} = props
    const {bvEnabled} = useOcapiSite()
    const recipe = useSlotRecipe({recipe: ratingsTextRecipe})
    const styles = useMemo(() => recipe({variant}), [recipe, variant])
    const ratingDisplayValue = useMemo(
        () => (typeof rating === 'number' ? rating.toFixed(1) : null),
        [rating]
    )

    if (!bvEnabled) {
        return null
    }

    if (rating == null) {
        return <RatingSkeleton {...props} />
    }

    const fullStars = Math.round(rating)
    const emptyStars = RATING_MAX_STARS - fullStars
    const ratingIsDefined = (fullStars && emptyStars) != (null || undefined)

    return (
        ratingIsDefined && (
            <Flex
                flexDir="row"
                css={styles.container}
                {...ratingsProps}
                data-testid="ratings"
                onClick={onRatingsClick}
                tabIndex="0"
                aria-label={formatMessage(
                    {
                        id: 'reviews.overview.aria_label',
                        defaultMessage:
                            '{rating} out of 5 stars, {reviews} {reviews, plural, one {review} other {reviews}}'
                    },
                    {
                        rating: rating.toFixed(1),
                        reviews: count
                    }
                )}
            >
                {ratingDisplayValue != null && (
                    <Text css={styles.ratingValue} data-testid="rating-value">
                        {ratingDisplayValue}
                    </Text>
                )}
                <Button
                    unstyled
                    css={styles.button}
                    aria-label={formatMessage(
                        {
                            id: 'reviews.rating_value',
                            defaultMessage: 'Review: {stars, plural, one {# star} other {# stars}}'
                        },
                        {stars: fullStars}
                    )}
                >
                    {Array.from({length: fullStars}).map((_, i) => (
                        <Box
                            key={i}
                            css={[styles.starContainer, styles.full]}
                            data-testid="full-star"
                        >
                            <StarFilledIcon />
                        </Box>
                    ))}
                    {Array.from({length: emptyStars}).map((_, i) => (
                        <Box key={i} css={styles.starContainer} data-testid="empty-star">
                            <StarIcon />
                        </Box>
                    ))}
                </Button>
                {count != null && (
                    <Text css={styles.ratingCount} data-testid="rating-count">
                        <VisuallyHidden>
                            {formatMessage(
                                {
                                    id: 'product_tile.assistive_msg.rating_count',
                                    defaultMessage: 'Number of reviews: {count}'
                                },
                                {
                                    count
                                }
                            )}
                        </VisuallyHidden>
                        <span aria-hidden="true">{`(${count})`}</span>
                    </Text>
                )}
            </Flex>
        )
    )
}

Ratings.propTypes = {
    count: PropTypes.number,
    rating: PropTypes.number,
    variant: PropTypes.string,
    onRatingsClick: PropTypes.func
}

export default Ratings
